import React, { useEffect, useState } from 'react'
import { Dropdown, MenuItem } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'

import CardContainer from 'live/components/ui/CardContainer'
import authorization from 'live/services/authorization'
import globalConfirmation from 'live/services/global-confirmation'
import i18n from 'live/services/i18n'

import { EntityErrorModal, FilteredSearch, IError, IListItemError, Scrollbars } from '../../../../../generic'
import { IReduxActions, IReduxReducers } from '../../../../../store'
import { IBarrier, IBowtie, IConsequence, IThreat } from '../../../../../types'
import { IType } from '../../BowtieEditor'
import { BowtieEditorListContainer } from './styles'

type IProps = {
    handleDeleteClick: () => void
    handleEditClick: (k: IBarrier, type: 'threat' | 'consequence', escId: string) => void
    data: IBowtie
    onEditThreatConsequence: (k: IThreat | IConsequence, type: 'threat' | 'consequence') => void
    errors: IError[]
    handleDeleteThreatConsequenceClick: (type: IType, index: number) => void
}

type Type = 'HAZARD' | 'TOP EVENT' | 'THREAT' | 'CONSEQUENCE' | 'CONTROL BARRIER' | 'ESCALATION FACTOR' | 'BARRIER'

type ListItem = {
    type: Type
    description: string
    id: string | number
    onEdit?: () => void
    onDelete?: () => void
}

export const BowtieEditorList: React.FC<IProps> = ({
    data,
    errors,
    handleEditClick,
    handleDeleteClick,
    onEditThreatConsequence,
    handleDeleteThreatConsequenceClick
}) => {
    const [selectedType, setSelectedType] = useState<Type | 'ALL'>('ALL')
    const [sort, setSort] = useState<'ASC' | 'DESC'>(null)
    const [searchedString, setSearchedString] = useState<string>('')
    const [errorsModal, setErrorsModal] = useState(false)
    const dispatch = useDispatch()
    const { consequencesExpandedBarriers, threatsExpandedBarriers } = useSelector(
        (state: IReduxReducers) => state.bowtieExpandedBarriers
    )

    const addBarrierTargetUid = useSelector((state: IReduxReducers) => state.addBarrierEditor.targetUid)

    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    useEffect(() => {
        if (errorsModal && addBarrierTargetUid != null) {
            setErrorsModal(false)
        }
    }, [addBarrierTargetUid])

    const handleDelete = (type: 'threat' | 'consequence', barrier: IBarrier, fatherIndex: number): void => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        let stringToDispatch: string

        if (type === 'threat') {
            const newBarriers = data.topEvent.threats[fatherIndex].barriers.filter(old => old !== barrier)
            data.topEvent.threats[fatherIndex].barriers = newBarriers
            stringToDispatch = `thr-${data.topEvent.threats[fatherIndex].id}|bar-${barrier.id}`
        }

        if (type === 'consequence') {
            const newBarriers = data.topEvent.consequences[fatherIndex].barriers.filter(old => old !== barrier)
            data.topEvent.consequences[fatherIndex].barriers = newBarriers
            stringToDispatch = `cnq-${data.topEvent.consequences[fatherIndex].id}|bar-${barrier.id}`
        }

        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: expandedBarriers.filter(el => !el.barrierId.startsWith(stringToDispatch))
            }
        })
        handleDeleteClick()
    }

    const handleSort = (a: ListItem, b: ListItem): number => {
        if (sort) {
            if (a.description < b.description) {
                return sort === 'ASC' ? -1 : 1
            }

            if (a.description > b.description) {
                return sort === 'ASC' ? 1 : -1
            }
        }

        return 0
    }

    function mountBarriers(
        bar: IBarrier,
        rootTypeId: string,
        rootType: 'threat' | 'consequence',
        rootIndex: number,
        isPrimary: boolean
    ): ListItem[] {
        let items: ListItem[] = []

        if (bar) {
            items.push({
                id: bar.id,
                type: isPrimary ? 'BARRIER' : 'CONTROL BARRIER',
                description: bar.name ?? '',
                onEdit: () => isPrimary && handleEditClick(bar, rootType, `${rootTypeId}|bar-${bar.id}`),
                onDelete: () => isPrimary && handleDelete(rootType, bar, rootIndex)
            })

            if (bar.escalations) {
                for (const escalation of bar.escalations) {
                    if (escalation) {
                        items.push({
                            type: 'ESCALATION FACTOR',
                            description: escalation.name ?? '',
                            id: escalation.id
                        })

                        if (escalation.barriers) {
                            for (const escBarrier of escalation.barriers) {
                                if (escBarrier) {
                                    items = [
                                        ...items,
                                        ...mountBarriers(escBarrier, rootTypeId, rootType, rootIndex, false)
                                    ]
                                }
                            }
                        }
                    }
                }
            }
        }

        return items
    }

    const mountList = (): ListItem[] => {
        let items: ListItem[] = []

        items.push({
            id: data.topEvent.hazard.id,
            description: data.topEvent.hazard.name ?? '',
            type: 'HAZARD'
        })

        items.push({
            id: data.topEvent.id,
            description: data.topEvent.name ?? '',
            type: 'TOP EVENT'
        })

        for (const [idx, consequence] of data.topEvent.consequences.entries()) {
            items.push({
                id: consequence.id,
                description: consequence.name ?? '',
                type: 'CONSEQUENCE',
                onEdit: () => onEditThreatConsequence(consequence, 'consequence'),
                onDelete:
                    data.topEvent.consequences.length > 1
                        ? () => handleDeleteThreatConsequenceClick('consequence', idx)
                        : undefined
            })

            for (const barrier of consequence.barriers) {
                items = [...items, ...mountBarriers(barrier, `cnq-${consequence.id}`, 'consequence', idx, true)]
            }
        }

        for (const [idx, threat] of data.topEvent.threats.entries()) {
            items.push({
                id: threat.id,
                description: threat.name ?? '',
                type: 'THREAT',
                onEdit: () => onEditThreatConsequence(threat, 'threat'),
                onDelete:
                    data.topEvent.threats.length > 1
                        ? () => handleDeleteThreatConsequenceClick('threat', idx)
                        : undefined
            })

            for (const barrier of threat.barriers) {
                items = [...items, ...mountBarriers(barrier, `thr-${threat.id}`, 'threat', idx, true)]
            }
        }

        return items
    }

    const handleConfirmDelete = (item: ListItem) => {
        return () =>
            globalConfirmation.warn(
                i18n('barrier-removal-message', {
                    type: i18n(item.type).toLowerCase(),
                    name: item.description
                }),
                item.onDelete,
                { btnText: i18n('Remove') }
            )
    }

    return (
        <BowtieEditorListContainer>
            <div className="header">
                <div className="header-title">{i18n('LIST OF ENTITIES')}</div>
                <div className="header-subtitle">
                    {mountList().length ?? 0} {i18n('items')}
                </div>
                <button className="header-error-alert" onClick={() => errors.length && setErrorsModal(true)}>
                    <i className={`far fa-bell ${errors.length > 0 ? 'has-errors' : ''}`}></i>
                </button>
            </div>
            <div className="row-div">
                <FilteredSearch onSearch={setSearchedString} keyword={searchedString} />
                <Dropdown pullRight id="sort-button">
                    <Dropdown.Toggle>
                        <i className="far fa-sort-alt" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <MenuItem active={!sort} onClick={() => setSort(null)}>
                            {i18n('Default')}
                        </MenuItem>
                        <MenuItem active={sort === 'ASC'} onClick={() => setSort('ASC')}>
                            {i18n('Ascending')}
                        </MenuItem>
                        <MenuItem active={sort === 'DESC'} onClick={() => setSort('DESC')}>
                            {i18n('Descending')}
                        </MenuItem>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <div className="type-bar">
                <div className={selectedType === 'ALL' ? 'active' : ''} onClick={() => setSelectedType('ALL')}>
                    {i18n('All')}
                </div>
                <div className={selectedType === 'THREAT' ? 'active' : ''} onClick={() => setSelectedType('THREAT')}>
                    {i18n('Threats')}
                </div>
                <div
                    className={selectedType === 'CONSEQUENCE' ? 'active' : ''}
                    onClick={() => setSelectedType('CONSEQUENCE')}
                >
                    {i18n('Consequences')}
                </div>
                <div className={selectedType === 'BARRIER' ? 'active' : ''} onClick={() => setSelectedType('BARRIER')}>
                    {i18n('Barriers')}
                </div>
                <div
                    className={selectedType === 'ESCALATION FACTOR' ? 'active' : ''}
                    onClick={() => setSelectedType('ESCALATION FACTOR')}
                >
                    {i18n('Escalation Factors')}
                </div>
            </div>
            <div className="bowtie-list">
                <Scrollbars>
                    {mountList()
                        .sort(handleSort)
                        .filter(item => (selectedType === 'ALL' ? true : item.type.includes(selectedType)))
                        .filter(item => item.description.toLowerCase().includes(searchedString.toLowerCase()))
                        .map((item, idx) => (
                            <CardContainer
                                key={`${idx}-${item.id}`}
                                className={`custom-card ${item.type.replace(' ', '')}`}
                            >
                                <div className="main-row" onClick={item.onEdit}>
                                    <div className={`main-col custom-card-border ${item.type.replace(' ', '-')}`} />
                                    <div className="main-col info">
                                        <div className="row-card">
                                            <div className="title">{i18n(item.type)}</div>
                                            <div className="id">{item.id ? `#${item.id}` : ''}</div>
                                        </div>
                                        <div className="card-description">{item.description || '-'}</div>
                                    </div>
                                </div>
                                {bowtieAdmin && item.onDelete && (
                                    <div className="delete-button" onClick={handleConfirmDelete(item)}>
                                        <i className="fal fa-trash-alt" />
                                    </div>
                                )}
                            </CardContainer>
                        ))}
                </Scrollbars>
            </div>

            <EntityErrorModal
                onClose={() => setErrorsModal(false)}
                show={errorsModal}
                errorsList={errors.map(
                    (error): IListItemError => ({
                        type: error.type,
                        errors: error.errors,
                        name: error.name,
                        onEdit: error.onEdit
                    })
                )}
            />
        </BowtieEditorListContainer>
    )
}
